import './menu.scss';

import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';

import { Layout, Skeleton } from 'antd';
import { desktopMinWidth } from 'lib/constants/media-queries';

import { Role } from 'features/auth/types';
import { RootState } from 'lib/store/rootReducer';

import { toggleMenuCollapse, UIPrefActions } from '../utils/navigationSlice';
import { ClinicianMenu } from './menu-clinician';
import { PatientSubMenu } from './menu-clinician-patient-submenu';
import { PatientMenu } from './menu-patient';
import { SideMenuFooter } from './menu-side-footer';

import { resolveRenderNavigation } from 'app/resolveRenderNavigation';
import { User } from 'features/auth';
import CRxSmall from 'img/crx-small.png';
import ConcussionLogo from 'img/sidebar-logo.svg';
import { getAppVersion } from 'lib/api/firebase/api-_utils';
import { useAuth } from 'lib/hooks/auth/use-auth';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function ConditionalNavigationMenu() {
  const auth = useAuth();
  const location = useLocation().pathname;
  /**
   * Navigation should be rendered when:
   * - The user is logged in
   * - The current route is not in the ommitted routes list
   * - **(2024-01-29) The user is not a patient. Patients will not see the side menu.
   */
  const shouldRenderNavigation = resolveRenderNavigation(auth.user, location);

  if (!shouldRenderNavigation) return null;
  return <NavigationMenu />;
}

/**
 * The sidebar of the app
 */
export function NavigationMenu() {
  const user = useSelector((state: RootState) => state.auth.user);
  const collapsed = useSelector(
    (state: RootState) => state.uiPref.menuCollapsed
  );
  const isLoading = useSelector(
    (state: RootState) => state.auth.isUserDataLoading
  );
  const isDesktop = useMediaQuery(desktopMinWidth);

  const location = useLocation();
  const dispatch = useDispatch();
  const isInPatientRoute = location.pathname.includes('/patient');
  useEffect(() => {
    if (isInPatientRoute) {
      dispatch(UIPrefActions.collapseMenu());
    } else {
      dispatch(UIPrefActions.openMenu());
    }
  }, [isInPatientRoute, dispatch]);

  /**
   * Do not show a menu if the user is not signed in, or needs to set their password
   */
  if (!user || user.needsPassword) return <></>;
  return (
    <>
      <Layout.Sider
        trigger={null}
        theme="light"
        onCollapse={collapsed =>
          dispatch(
            collapsed ? UIPrefActions.collapseMenu() : UIPrefActions.openMenu()
          )
        }
        collapsedWidth={isDesktop ? 80 : 80}
        width={236}
        collapsible
        collapsed={collapsed}
        className="crx-sider"
        style={{
          borderRadius: isInPatientRoute ? 0 : undefined
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            paddingBottom: '0rem'
          }}
        >
          {collapsed ? (
            <img
              style={{ width: '100%', maxHeight: 50, maxWidth: 50 }}
              src={CRxSmall}
            />
          ) : (
            <img src={ConcussionLogo} />
          )}
        </div>

        <BuildVersion />
        <SideMenu user={user} isLoading={isLoading} />
      </Layout.Sider>

      {isInPatientRoute &&
        [Role.Clinician, Role.ClinicAdmin].includes(user.role) && (
          <Layout.Sider
            trigger={null}
            theme="light"
            width={236}
            className="crx-sider"
          >
            <PatientSubMenu />
          </Layout.Sider>
        )}
    </>
  );
}

export function BuildVersion() {
  return (
    <p
      style={{
        color: 'gray',
        textAlign: 'center',
        fontSize: 10
      }}
    >
      Build: {getAppVersion()?.slice(0, 8)}
    </p>
  );
}

export function SideMenu(props: { user: User; isLoading: boolean }) {
  const collapsed = useSelector(
    (state: RootState) => state.uiPref.menuCollapsed
  );

  const dispatch = useDispatch();
  return (
    <div className="rx-sidemenu">
      <div>
        {[Role.Clinician, Role.ClinicAdmin].includes(props.user.role) && (
          <ClinicianMenu />
        )}
        {props.user.role === Role.Patient && <PatientMenu />}
      </div>
      {props.isLoading && <Skeleton />}

      <div>
        <SideMenuFooter
          collapsed={collapsed}
          onClick={() => {
            dispatch(toggleMenuCollapse());
          }}
          user={props.user}
        />
      </div>
    </div>
  );
}
